import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { latexAPI } from '@/api/latex'
import { logError, logDebug } from '@utils/logger'

// Cache for citation search results
const citationCache: Map<string, { citations: any[]; timestamp: number }> = new Map()
const CACHE_TTL = 30000 // 30 seconds

/**
 * Citation autocomplete provider for LaTeX \cite{} commands
 * Detects \cite{ pattern and provides suggestions from project citations
 */
export function citationAutocompletion(documentId: string | null | undefined) {
  return async (context: CompletionContext): Promise<CompletionResult | null> => {
    // Only work in LaTeX mode
    if (!documentId) return null
    
    const { state, pos } = context
    const text = state.doc.toString()
    
    // Look backwards from cursor to find \cite{ pattern
    // Pattern: \cite{...} where we're inside the braces
    let start = pos - 1
    let depth = 0
    let foundCite = false
    let citeStart = pos
    
    // Scan backwards to find \cite{
    while (start >= 0 && (pos - start) <= 100) {
      const char = text[start]
      
      if (char === '}') {
        depth++
      } else if (char === '{') {
        if (depth === 0) {
          // Found opening brace - check if preceded by \cite
          const beforeBrace = text.substring(Math.max(0, start - 6), start)
          if (beforeBrace.endsWith('\\cite')) {
            foundCite = true
            citeStart = start + 1
            break
          } else {
            // Not a \cite command, abort
            return null
          }
        } else {
          depth--
        }
      }
      
      start--
    }
    
    if (!foundCite) {
      return null
    }
    
    // Extract the current query (text after {)
    const query = text.substring(citeStart, pos).trim()
    
    logDebug('[citationAutocomplete] Detected \\cite{ pattern', { query, citeStart, pos })
    
    // Check cache first
    const cacheKey = `${documentId}:${query}`
    const cached = citationCache.get(cacheKey)
    const now = Date.now()
    
    let citations: any[] = []
    
    if (cached && (now - cached.timestamp) < CACHE_TTL) {
      citations = cached.citations
      logDebug('[citationAutocomplete] Using cached citations', { count: citations.length })
    } else {
      try {
        // Fetch citations from API
        const result = await latexAPI.searchCitations(documentId, query || undefined, 20)
        citations = result.citations || []
        
        // Cache the results
        citationCache.set(cacheKey, { citations, timestamp: now })
        logDebug('[citationAutocomplete] Fetched citations from API', { count: citations.length })
      } catch (error) {
        logError('[citationAutocomplete] Failed to fetch citations:', error)
        return null
      }
    }
    
    if (citations.length === 0) {
      return null
    }
    
    // Build completion options
    const options = citations.map((citation) => {
      const key = citation.key || ''
      const title = citation.title || 'Untitled'
      const authors = citation.authors || 'Unknown'
      const year = citation.year || ''
      
      // Build display label
      let info = title
      
      if (authors !== 'Unknown' || year) {
        info = `${authors}${year ? ` (${year})` : ''} - ${title}`
      }
      
      return {
        label: key,
        type: 'variable' as const,
        info: info.length > 100 ? info.substring(0, 100) + '...' : info,
        apply: key, // Just insert the key
      }
    })
    
    return {
      from: citeStart,
      to: pos,
      options,
      filter: false, // Don't filter - we already filtered on the backend
      span: /^[a-zA-Z0-9_:.-]*$/, // Valid characters for citation keys
      validFor: /^[a-zA-Z0-9_:.-]*$/, // Completion is valid while typing citation key
    } as CompletionResult & { span: RegExp; validFor: RegExp }
  }
}

/**
 * Clear citation cache (useful after adding new citations)
 */
export function clearCitationCache(documentId?: string) {
  if (documentId) {
    // Clear cache for specific document
    const keysToDelete: string[] = []
    for (const key of citationCache.keys()) {
      if (key.startsWith(`${documentId}:`)) {
        keysToDelete.push(key)
      }
    }
    keysToDelete.forEach(key => citationCache.delete(key))
  } else {
    // Clear all cache
    citationCache.clear()
  }
}

